import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, ChevronLeft, Pencil } from "lucide-react";
import {
  sendPasswordPgw,
  loginToken,
  setUserEntry,
  sendPasswordVerify,
  confirmPasswordReset,
} from "../../server/Customer";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import Input from "../containers/InputForm";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const schema = yup
  .object({
    userName: yup.string().required("שדה חובה").trim(),
    password: yup.string().required("חובה להכניס סיסמה"),
  })
  .required();

const resetSchema = yup.object({
  newPassword: yup
    .string()
    .required("חובה להכניס סיסמה")
    .min(8, "לפחות 8 תווים")
    .max(72, "מקסימום 72 תווים"),
  confirmPassword: yup
    .string()
    .required("חובה לאשר סיסמה")
    .oneOf([yup.ref("newPassword")], "הסיסמאות לא תואמות"),
});

const UserLogin = ({
  onInlineError,
  onClearInlineError,
  onSwitchToRegister,
}) => {
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
  } = useForm({
    resolver: yupResolver(resetSchema),
  });

  const [email, setEmail] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [forgotPass, setForgotPass] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [userId, setUserId] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [validationCode, setValidationCode] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const emailInputRef = useRef(null);

  useEffect(() => {
    if (editingEmail) {
      emailInputRef.current?.focus();
      emailInputRef.current?.select();
    }
  }, [editingEmail]);

  const sendPass = () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      onInlineError?.("יש להזין כתובת מייל.", "שגיאה");
      return;
    }

    onClearInlineError?.();
    setEmail(normalizedEmail);
    setEditingEmail(false);
    sendPasswordPgw(normalizedEmail, { suppressAlert: true })
      .then((res) => {
        setUserId(res.userId);
        setMaxAttempts(res.maxAttempts);
      })
      .catch(() =>
        onInlineError?.("לא הצלחנו לשלוח קוד איפוס. נסה שוב.", "שגיאה"),
      );
  };

  const verify = () => {
    if (attempts <= maxAttempts) {
      onClearInlineError?.();
      sendPasswordVerify(validationCode, userId, { suppressAlert: true })
        .then(() => {
          setIsVerified(true);
        })
        .catch(() => onInlineError?.("קוד האימות אינו תקין.", "שגיאה"));

      setAttempts((prev) => prev + 1);
    } else {
      onInlineError?.("ניסית יותר מדי פעמים. נסה שוב מאוחר יותר.", "שגיאה");
    }
  };

  const confirmPass = (data) => {
    onClearInlineError?.();
    confirmPasswordReset(userId, validationCode, data.newPassword, {
      suppressAlert: true,
    })
      .then(() => {
        setForgotPass(false);
        setIsVerified(false);
      })
      .catch(() => onInlineError?.("משהו השתבש. נסה שוב.", "שגיאה"));
  };

  const signIn = async (data) => {
    onClearInlineError?.();
    data.userName = data.userName.trim().toLowerCase();
    data["grant_type"] = "password";
    try {
      await dispatch(loginToken(data, true, { suppressAlert: true }));
    } catch (e) {
      onInlineError?.(
        e?.message || "שם משתמש וסיסמה שגויים",
        "פרטי ההתחברות לא תקינים",
      );
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassVisibility = () => {
    setShowConfirmPass(!showConfirmPass);
  };

  return !forgotPass ? (
    <form className="login-form" onSubmit={handleSubmit(signIn)}>
      <div className="hz-login-field hz-login-field--email">
        <Input
          errors={errors}
          label="דואר אלקטרוני"
          type="text"
          name="userName"
          register={register}
        />
      </div>

      <div className="hz-login-field hz-login-field--password">
        <div className="hz-login-password">
          <div className="input-wrapper">
            <input
              className="hz-login-password__input"
              type={showPassword ? "text" : "password"}
              {...register("password")}
              id="password"
              aria-label="password"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="hz-login-password__toggle"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
            >
              {showPassword ? (
                <EyeOff size={18} strokeWidth={2} aria-hidden />
              ) : (
                <Eye size={18} strokeWidth={2} aria-hidden />
              )}
            </button>
          </div>

          {errors?.password ? (
            <div className="text-danger">{errors.password.message}</div>
          ) : null}
        </div>
      </div>

      <button className="login-btn" type="submit">
        כניסה
        <ChevronLeft size={18} strokeWidth={2.4} aria-hidden />
      </button>

      <span
        onClick={() => {
          const loginEmail = getValues("userName");
          if (loginEmail) {
            setEmail(String(loginEmail).trim());
          }
          setForgotPass(true);
        }}
        className="forgot-pass"
      >
        שכחת את הסיסמה?
      </span>

      <button
        className="cancel"
        onClick={() => dispatch(setUserEntry([false, "login"]))}
      >
        כניסה ללא הרשמה
        <ChevronLeft size={14} strokeWidth={2.4} aria-hidden />
      </button>
    </form>
  ) : (
    <div className="forgot-pass-wrap">
      {!userId ? (
        <>
          <p>אנא הקלד את כתובת המייל לאיפוס סיסמה</p>
          <input
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="אימייל"
            value={email}
            aria-label="כתובת מייל"
            autoComplete="email"
            dir="ltr"
          />
          <button className="send-btn" onClick={sendPass}>
            שלח
          </button>
        </>
      ) : !isVerified ? (
        <>
          <p>
            קוד נשלח לכתובת המייל. ניתן לערוך אותה ולשלוח קוד חדש אם יש טעות.
          </p>
          <div className="forgot-pass-email-row">
            {editingEmail ? (
              <input
                ref={emailInputRef}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setEditingEmail(false);
                  }
                  if (e.key === "Escape") {
                    setEditingEmail(false);
                  }
                }}
                onBlur={() => setEditingEmail(false)}
                placeholder="אימייל"
                value={email}
                aria-label="כתובת מייל"
                autoComplete="email"
                dir="ltr"
                className="forgot-pass-email-input"
              />
            ) : (
              <>
                <span className="forgot-pass-email-value" dir="ltr">
                  {email || "—"}
                </span>
                <button
                  type="button"
                  className="forgot-pass-email-edit"
                  onClick={() => setEditingEmail(true)}
                  aria-label="עריכת כתובת מייל"
                  title="עריכת כתובת מייל"
                >
                  <Pencil size={15} strokeWidth={2} aria-hidden />
                </button>
              </>
            )}
          </div>
          <button
            type="button"
            className="forgot-pass-link"
            onClick={() => {
              setValidationCode("");
              setAttempts(0);
              sendPass();
            }}
          >
            שליחת קוד מחדש למייל זה
          </button>
          <p>אנא הקלד את הקוד שקיבלת</p>
          <input
            type="text"
            onChange={(e) => setValidationCode(e.target.value)}
            placeholder="קוד אימות"
            value={validationCode}
            aria-label="קוד אימות"
            inputMode="numeric"
            autoComplete="one-time-code"
          />
          <button className="send-btn" onClick={verify}>
            שלח
          </button>
        </>
      ) : (
        <form onSubmit={handleSubmitReset(confirmPass)}>
          <Input
            type={showPassword ? "text" : "password"}
            name="newPassword"
            label="סיסמה חדשה"
            className="password-input"
            toggleVisibility={togglePasswordVisibility}
            register={registerReset}
            errors={resetErrors}
            showPassword={showPassword}
            isPasswordField
          />

          <Input
            type={showConfirmPass ? "text" : "password"}
            name="confirmPassword"
            label="אימות סיסמה"
            toggleVisibility={toggleShowConfirmPassVisibility}
            register={registerReset}
            errors={resetErrors}
            showPassword={showConfirmPass}
            isPasswordField
          />

          <button className="send-btn" type="submit">
            שלח
          </button>
        </form>
      )}
    </div>
  );
};

export default UserLogin;
